import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  auth,
  db,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  type FirebaseUser,
} from '@/lib/firebase';
import type { User, AuthContextType } from '@/types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Load user data from Firestore
  const loadUserData = async (uid: string): Promise<User | null> => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));

      if (!userDoc.exists()) {
        console.error('User document not found in Firestore');
        return null;
      }

      const userData = userDoc.data();

      // Check if user is suspended
      if (userData.status === 'suspended') {
        await firebaseSignOut(auth);
        throw new Error('Account sospeso. Contatta l\'amministratore.');
      }

      // Check if user is still pending
      if (userData.status === 'pending') {
        await firebaseSignOut(auth);
        throw new Error('Account non ancora attivato. Completa la configurazione.');
      }

      // Check if activation link expired
      if (userData.status === 'expired') {
        await firebaseSignOut(auth);
        throw new Error('Il tuo link di attivazione è scaduto. Contatta l\'amministratore per ricevere un nuovo link.');
      }

      const user: User = {
        id: userDoc.id,
        email: userData.email,
        displayName: userData.displayName,
        photoURL: userData.photoURL,
        role: userData.role,
        status: userData.status,
        createdAt: userData.createdAt,
        lastLogin: userData.lastLogin,
        suspendedAt: userData.suspendedAt,
        suspendedBy: userData.suspendedBy,
        reactivatedAt: userData.reactivatedAt,
        reactivatedBy: userData.reactivatedBy,
      };

      return user;
    } catch (error) {
      console.error('Error loading user data:', error);
      throw error;
    }
  };

  // Update last login timestamp
  const updateLastLogin = async (uid: string) => {
    try {
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        lastLogin: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating last login:', error);
      // Don't throw - this is not critical
    }
  };

  // Sign in with email and password
  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const uid = userCredential.user.uid;

      // Load user data
      const userData = await loadUserData(uid);

      if (!userData) {
        await firebaseSignOut(auth);
        throw new Error('Impossibile caricare i dati utente.');
      }

      // Update last login (non-blocking)
      updateLastLogin(uid);

      setUser(userData);
      setFirebaseUser(userCredential.user);
    } catch (error: any) {
      console.error('Sign in error:', error);

      // User-friendly error messages
      let message = 'Errore durante l\'accesso.';

      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        message = 'Email o password non corretti.';
      } else if (error.code === 'auth/too-many-requests') {
        message = 'Troppi tentativi falliti. Riprova più tardi.';
      } else if (error.code === 'auth/user-disabled') {
        message = 'Account disabilitato. Contatta l\'amministratore.';
      } else if (error.message) {
        message = error.message;
      }

      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      setLoading(true);
      await firebaseSignOut(auth);
      setUser(null);
      setFirebaseUser(null);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Refresh user data from Firestore
  const refreshUserData = async () => {
    if (!firebaseUser) return;

    try {
      const userData = await loadUserData(firebaseUser.uid);
      if (userData) {
        setUser(userData);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const userData = await loadUserData(firebaseUser.uid);
          if (userData) {
            setUser(userData);
            setFirebaseUser(firebaseUser);
          }
        } catch (error) {
          console.error('Error in auth state change:', error);
          setUser(null);
          setFirebaseUser(null);
        }
      } else {
        setUser(null);
        setFirebaseUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const value: AuthContextType = {
    user,
    firebaseUser,
    loading,
    signIn,
    signOut,
    refreshUserData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
